import axios from 'axios';

import { assertClientSide } from '@dr.pogodin/react-utils';

import type {
  AgentStartResT,
  SignalListenerT,
} from 'client/services/ai/realtime';

import type { UseSessionResT } from 'hooks';

import type {
  SaveAiInterviewAsQuestionnaireBodyT,
  SaveAiInterviewAsQuestionnaireResT,
} from 'server/controllers/questionnaire';

import { getRealtimeAiClient } from 'services/getClient';

import {
  type ExperienceT,
  P_API_QUESTIONNAIRE_FROM_INTERVIEW,
  type QuestionnaireKinds,
  type SessionKindT,
  type TranscriptT,
  type VoiceT,
  getConfig,
} from 'utils';

export type ModeT = 'ONBOARDING' | 'WELLBEING_CHECK_IN' | 'WELLBEING_CHECK_OUT';

export async function startAgent(
  mode: ModeT,
  voice: VoiceT,
  onSignal: SignalListenerT,
  userId: string,
  experience: ExperienceT | null | undefined,
  session: UseSessionResT,
  sessionKind: SessionKindT | null | undefined,
): Promise<AgentStartResT> {
  const realtime = await getRealtimeAiClient();

  switch (mode) {
    case 'ONBOARDING':
      return realtime.startOnboarding(voice, onSignal);

    case 'WELLBEING_CHECK_IN':
      return realtime.startWellnessCheckIn(voice, onSignal, userId);

    case 'WELLBEING_CHECK_OUT':
      if (!experience) throw Error('Internal error');
      return realtime.startSessionCheckOut({
        experience,
        onSignal,
        session,
        sessionKind: sessionKind ?? null,
        userId,
        voice,
      });
    default: throw Error('Unexpected case');
  }
}

type ProcessInterviewArgT = {
  completedAt?: number;

  // TODO: Perhaps, type it better, making `sessionId` required for the post
  // session interview kind, and not necessary for wellness check-in one.
  kind:
    | QuestionnaireKinds.ONBOARDING_AI_INTERVIEW
    | QuestionnaireKinds.POST_SESSION__AI_REALTIME
    | QuestionnaireKinds.WELLNESS_CHECKIN__AI_REALTIME;

  sessionId: string | undefined;
  startedAt: number;
  transcript: TranscriptT;
  userDateTime: string;
  userId: string;
};

type ProcessInterviewResT = {
  questionnaireId: string;
  transcriptId: string;
};

export async function processInterview({
  completedAt = Date.now(),
  kind,
  sessionId,
  startedAt,
  transcript,
  userDateTime,
  userId,
}: ProcessInterviewArgT): Promise<ProcessInterviewResT> {
  assertClientSide();

  const config = await getConfig();

  const res = await axios.post<SaveAiInterviewAsQuestionnaireResT>(
    P_API_QUESTIONNAIRE_FROM_INTERVIEW,
    {
      _csrf: config.CSRF,
      completedAt,
      kind,
      sessionId,
      startedAt,
      transcript,
      userDateTime,
      userId,
    } satisfies SaveAiInterviewAsQuestionnaireBodyT,
  );

  return res.data;
}
